import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { VaultAuthService } from '../services/vault-auth.service';
import { VaultNoticeService } from '../services/vault-notice.service';
import { isSecurePath, toPathname } from '../crypto/secure-channel.service';

/** Endpoints that must not carry a token, or that would recurse if a 401 triggered a refresh. */
const AUTH_ENDPOINTS = ['/api/family/auth/login', '/api/family/auth/verify', '/api/family/auth/refresh'];

/**
 * Attaches the vault's in-memory access token and recovers from expiry.
 *
 * Separate from the committee module's `authInterceptor`, which reads its token from localStorage.
 * Sending that token to a vault endpoint would be wrong twice over — it belongs to a different
 * login flow, and the whole point of the in-memory token is that nothing reaches for storage.
 *
 * On a 401 the refresh cookie is exchanged for a new access token and the request is replayed
 * once. A second failure means the session is genuinely over, so the user is sent to sign in.
 */
export const vaultAuthInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isSecurePath(req.url)) {
    return next(req);
  }

  const auth = inject(VaultAuthService);
  const router = inject(Router);
  const notices = inject(VaultNoticeService);

  const path = toPathname(req.url);
  if (AUTH_ENDPOINTS.includes(path)) {
    return next(req);
  }

  const token = auth.getAccessToken();
  const authorized = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` }, withCredentials: true })
    : req.clone({ withCredentials: true });

  return next(authorized).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      return from(auth.restore()).pipe(
        switchMap(restored => {
          if (!restored) {
            // Said before the navigation, not after: being moved to a sign-in screen mid-action
            // with no explanation reads as the app having lost the work, which it has not.
            notices.info('Your session ended', 'Sign in again to continue where you left off.');

            router.navigate(['/vault/sign-in'], {
              queryParams: { returnUrl: router.url },
            });
            return throwError(() => error);
          }

          return next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${auth.getAccessToken()}` },
              withCredentials: true,
            }),
          );
        }),
      );
    }),
  );
};
